export type GameStatus = "setup" | "active" | "finished";
export type GameMode = "free" | "strict";
export type ScoreVisibility = "realtime" | "final_only";

export interface User {
  id: string;
  telegram_id: number | null;
  username: string | null;
  first_name: string | null;
  email?: string | null;
  photo_url?: string | null;
  colorful_dice: boolean;
  sound_enabled: boolean;
  table_theme: string;
  elo: number;
  dice_colors?: Record<number, string> | null; // cleanup later
  achievements?: {
    achievement: {
        id: string;
        name_en: string;
        name_ru: string;
        description_en: string;
        description_ru: string;
        icon: string;
    };
    earned_at: string;
  }[];
}

export interface PlayerProfile {
  user_id: string;
  display_name: string;
  avatar: string | null;
  elo: number;
  games_played: number;
  wins: number;
  win_rate: number;
  avg_score: number;
  best_score: number;
}

export interface GamePlayer {
  id: string;
  session_id: string;
  user_id: string | null;
  telegram_id: number | null;
  display_name: string;
  avatar: string | null;
  seat_index: number;
  is_admin: boolean;
}


export interface Scorecard {
  player_id: string;
  session_id: string;
  // School — stored as balance: (dice_count − 3) × face_value
  ones: number | null;
  twos: number | null;
  threes: number | null;
  fours: number | null;
  fives: number | null;
  sixes: number | null;
  // Combinations — stored as actual score; 0 = crossed out
  one_pair: number | null;
  two_pairs: number | null;
  three_of_kind: number | null;
  straight1: number | null;
  straight2: number | null;
  full_house: number | null;
  four_of_kind: number | null;
  poker: number | null;
  garbage: number | null;
  // Categories scored "from hand" (first throw, ×2) — values already doubled;
  // used only to highlight lucky cells in the table.
  from_hand?: string[];
  updated_at?: string | null;
}

export interface ScoreTotals {
  school_sum: number;
  school_bonus: number;
  school_total: number;
  school_complete: boolean;
  combo_sum: number;
  combo_completion_bonus: number;
  combo_total: number;
  combo_complete: boolean;
  grand_total: number;
}

export interface PlayerWithScorecard {
  player: GamePlayer;
  scorecard: Scorecard;
  totals: ScoreTotals;
  // Present only on redacted (final_only) payloads: names of filled categories.
  // The server nulls other players' cell values, so use isCellFilled/filledCount
  // below instead of checking scorecard values directly.
  filled?: string[];
}

/** True if the category has a value — works on redacted payloads too. */
export function isCellFilled(p: PlayerWithScorecard, cat: Category): boolean {
  if (p.scorecard[cat] !== null && p.scorecard[cat] !== undefined) return true;
  return p.filled?.includes(cat) ?? false;
}

/** Number of filled cells for a player — works on redacted payloads too. */
export function filledCount(p: PlayerWithScorecard): number {
  return [...SCHOOL_CATEGORIES, ...COMBO_CATEGORIES].filter((c) =>
    isCellFilled(p, c),
  ).length;
}

export interface GameSession {
  id: string;
  invite_code: string;
  status: GameStatus;
  game_mode: GameMode;
  score_visibility: ScoreVisibility;
  created_by: string;
  created_at: string;
  finished_at?: string | null;
  players: GamePlayer[];

  // ── Dice rolling state ──
  current_turn_player_id?: string | null;
  current_dice?: number[] | null;
  dice_holds?: boolean[] | null;
  roll_count: number;
}

export interface SessionState {
  session: GameSession;
  players_with_scorecards: PlayerWithScorecard[];
}

export type WsEvent =
  | {
      type: "session_state";
      session: GameSession;
      players: PlayerWithScorecard[];
    }
  | {
      type: "score_updated";
      session_id: string;
      player_id: string;
      category: string;
      value: number | null;
      totals: ScoreTotals;
      from_hand?: boolean;
      changed_by?: { user_id: string; display_name: string };
    }
  | { type: "player_added"; state: SessionState }
  | { type: "player_removed"; state: SessionState }
  | { type: "game_started"; state: SessionState }
  | {
      type: "game_finished";
      final_scores: {
        player_id: string;
        display_name: string;
        grand_total: number;
      }[];
      new_achievements?: Record<string, {
        id: string;
        name_en: string;
        name_ru: string;
        icon: string;
      }[]>; // Map of user_id -> [achievements]
    }
  | { type: "rematch"; new_invite_code: string }
  | { type: "settings_updated"; score_visibility: ScoreVisibility }
  | { type: "reaction"; emoji: string; from: string }
  | { type: "error"; message: string };

export interface Achievement {
  id: string;
  name_en: string;
  name_ru: string;
  description_en: string;
  description_ru: string;
  icon: string;
  category: string;
  earned_at: string | null;
}

export interface RecentSession {
  id: string;
  invite_code: string;
  status: GameStatus;
  created_at: string;
  type?: "game" | "tournament";
  name?: string;
  total_games?: number;
  current_game?: number;
  players: {
    player_id: string;
    display_name: string;
    avatar: string | null;
    grand_total: number;
    is_you: boolean;
  }[];
}

export interface Contact {
  user_id: string;
  telegram_id: number | null;
  display_name: string;
  avatar: string | null;
}

export interface HeadToHeadStats {
  player1_id: string;
  player2_id: string;
  shared_games: number;
  player1_wins: number;
  player2_wins: number;
  draws: number;
}

export type SchoolCategory =
  | "ones"
  | "twos"
  | "threes"
  | "fours"
  | "fives"
  | "sixes";
export type ComboCategory =
  | "one_pair"
  | "two_pairs"
  | "three_of_kind"
  | "straight1"
  | "straight2"
  | "full_house"
  | "four_of_kind"
  | "poker"
  | "garbage";
export type Category = SchoolCategory | ComboCategory;

export const SCHOOL_CATEGORIES: SchoolCategory[] = [
  "ones",
  "twos",
  "threes",
  "fours",
  "fives",
  "sixes",
];
export const COMBO_CATEGORIES: ComboCategory[] = [
  "one_pair",
  "two_pairs",
  "three_of_kind",
  "straight1",
  "straight2",
  "full_house",
  "four_of_kind",
  "poker",
  "garbage",
];
